import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { SHARING_AUDIO_MESSAGE_HIGHLIGHT_CLASSNAME } from '@modules/feedback/components/AudioType/AudioTranscript/constants';
import {
  getMessageIndicesFromSnippetTime,
  getValidAudioDuration,
  highlightSharingAudioTranscriptMessage,
  removeFeedbackMessageHighlight,
} from '@modules/feedback/components/AudioType/utils';
import useFeedbackListStore, {
  FeedbackListStore,
} from '@modules/feedback/store/useFeedbackListStore';
import { useEffect } from 'react';
import { FeedbackLabel } from 'v2/core/entities/feedback';
import { AudioConversationMessage } from 'v2/core/entities/feedback/content';
import { UUID } from 'v2/core/entities/uuid';
import { shallow } from 'zustand/shallow';

const getFeedbackListStoreState = (
  state: FeedbackListStore,
  feedbackID: UUID,
) => {
  return {
    setSnippetTimeToPlay: state.setSnippetTimeToPlay,
    setIsTranscriptPopoverExpanded: state.setIsTranscriptPopoverExpanded,
    duration: state.feedbacks?.[feedbackID]?.audioDuration ?? 0,
    snippetTimeToPlay: state.feedbacks?.[feedbackID]?.snippetTimeToPlay,
    isTranscriptPopoverExpanded:
      state.feedbacks[feedbackID]?.isTranscriptPopoverExpanded ?? false,
    selectedAudioSnippet: state.feedbacks[feedbackID]?.selectedAudioSnippet,
  };
};

interface AudioFeedbackSnippetShareParams {
  feedbackID: UUID;
  feedbackLabels: FeedbackLabel[] | undefined;
  conversation: AudioConversationMessage[];
  isFeedbackCardView: boolean;
  isPlaying: boolean;
  onJumpSeek: (time: number) => void;
  onTogglePlay: (options?: { shouldSyncSeekValue: boolean }) => void;
  setSelectedLabels: (selectedLabels: FeedbackLabel[]) => void;
  isEmptyAudioSource: boolean;
}

export const useAudioFeedbackSnippetShareControls = ({
  feedbackID,
  feedbackLabels,
  conversation,
  isFeedbackCardView,
  isPlaying,
  onJumpSeek,
  onTogglePlay,
  setSelectedLabels,
  isEmptyAudioSource,
}: AudioFeedbackSnippetShareParams) => {
  const { showErrorNotification } = useNotificationContext();

  const {
    duration,
    snippetTimeToPlay,
    isTranscriptPopoverExpanded,
    selectedAudioSnippet,
    setIsTranscriptPopoverExpanded,
    setSnippetTimeToPlay,
  } = useFeedbackListStore(
    (state) => getFeedbackListStoreState(state, feedbackID),
    shallow,
  );

  const validDuration = getValidAudioDuration(duration, conversation);

  const queryParams = new URLSearchParams(window.location.search);
  const startTimeParam =
    queryParams.get('start') ?? selectedAudioSnippet?.startTime;
  const endTimeParam = queryParams.get('end') ?? selectedAudioSnippet?.endTime;
  const labelIDParam = queryParams.get('labelID');

  useEffect(() => {
    // consume the shared snippet query params
    if (
      isFeedbackCardView ||
      startTimeParam === undefined ||
      endTimeParam === undefined
    ) {
      return;
    }

    const snippetStartTime =
      typeof startTimeParam === 'string'
        ? parseFloat(startTimeParam)
        : startTimeParam;

    const snippetEndTime =
      typeof endTimeParam === 'string'
        ? parseFloat(endTimeParam)
        : endTimeParam;

    const isNanTime = isNaN(snippetStartTime) || isNaN(snippetEndTime);
    const isInvalidTimeRange =
      snippetStartTime >= snippetEndTime || snippetEndTime > validDuration;

    if (isNanTime || isInvalidTimeRange) {
      showErrorNotification({
        contentComponent: 'Shared snippet link is invalid.',
        props: {},
      });
      return;
    }

    setSnippetTimeToPlay(
      {
        startTime: parseFloat(snippetStartTime.toFixed(2)),
        endTime: parseFloat(snippetEndTime.toFixed(2)),
      },
      feedbackID,
    );

    if (labelIDParam) {
      const labelToSelect = feedbackLabels?.find(
        (label) => label.id === labelIDParam,
      );
      setSelectedLabels(labelToSelect ? [labelToSelect] : []);
      return;
    }

    if (
      !isTranscriptPopoverExpanded &&
      !labelIDParam &&
      !feedbackLabels?.length
    ) {
      setIsTranscriptPopoverExpanded(true, feedbackID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endTimeParam,
    feedbackID,
    isFeedbackCardView,
    startTimeParam,
    labelIDParam,
    feedbackLabels,
  ]);

  useEffect(() => {
    // jump to seek for playing shared/selected snippet
    if (
      isEmptyAudioSource ||
      isFeedbackCardView ||
      !snippetTimeToPlay?.startTime
    ) {
      return;
    }
    onJumpSeek(snippetTimeToPlay.startTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    feedbackID,
    isFeedbackCardView,
    snippetTimeToPlay?.startTime,
    isEmptyAudioSource,
  ]);

  useEffect(() => {
    // highlight the shared/sharing snippet messages
    if (isFeedbackCardView) {
      return;
    }

    if (!snippetTimeToPlay) {
      removeFeedbackMessageHighlight(SHARING_AUDIO_MESSAGE_HIGHLIGHT_CLASSNAME);
      return;
    }

    const snippetMessageIndices = getMessageIndicesFromSnippetTime(
      conversation,
      {
        startTime: snippetTimeToPlay.startTime,
        endTime: snippetTimeToPlay.endTime,
      },
    );

    highlightSharingAudioTranscriptMessage(snippetMessageIndices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversation,
    isFeedbackCardView,
    snippetTimeToPlay?.endTime,
    snippetTimeToPlay?.startTime,
    isTranscriptPopoverExpanded,
    feedbackID,
  ]);
};
